import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Trophy, Users } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { tournamentAPI } from '../services/tournamentApi';
import { apiCall } from '../../services/apiUtil';
import { moveImage } from '../../services/imageUploadService';
import TournamentForm from '../components/TournamentForm';
import ImageCropModal from '../../components/common/ImageCropModal';

export default function CreateTournamentPage() {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const tournamentFormRef = useRef(null);

  // Squad/group context — set when navigating from a squad page via location state
  const squadOwnerType = navState?.ownerType;
  const squadOwnerId = navState?.ownerId;
  const squadOwnerName = navState?.ownerName;
  const squadSportTypeId = navState?.sportTypeId;
  const isSquadContext = !!(squadOwnerType && squadOwnerId);

  // Image crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);

  // Pending image URL — stored when user crops before tournament is created (already uploaded + moderated).
  // Moved to canonical path after the tournament ID is known.
  const [pendingImageUrl, setPendingImageUrl] = useState(null);

  // Called by TournamentForm when user selects an image file
  const handleImageSelected = (dataUrl) => {
    setSelectedImageForCrop(dataUrl);
    setCropModalOpen(true);
  };

  // Handle approved image from modal — store temp URL and update form preview
  const handleCroppedImage = ({ url }) => {
    setPendingImageUrl(url);
    if (tournamentFormRef.current) {
      tournamentFormRef.current.applyApprovedImage({ url });
    }
  };

  const handleSubmit = async (formData) => {
    // Basic validation — dates not required when playNow is true
    if (!formData.tournamentName || !formData.sportTypeId) {
      toast.error('Please fill in all required fields (Tournament Name, Sport)');
      return;
    }

    if (!formData.playNow && (!formData.startDate || !formData.endDate)) {
      toast.error('Please fill in Start Date and End Date, or enable "Play Now"');
      return;
    }

    // Participant type validation
    if (!formData.participantsType) {
      toast.error('Please select a participant type');
      return;
    }

    // Compute dates for playNow mode
    let startDate = formData.startDate;
    let endDate = formData.endDate;
    if (formData.playNow) {
      const now = new Date();
      startDate = now.toISOString();
      endDate = new Date(now.getTime() + 4 * 60 * 60 * 1000).toISOString();
    }

    if (!formData.playNow && new Date(startDate) >= new Date(endDate)) {
      toast.error('End date must be after start date');
      return;
    }

    if (!formData.playNow && formData.registrationDeadline && new Date(formData.registrationDeadline) >= new Date(startDate)) {
      toast.error('Registration deadline must be before tournament start date');
      return;
    }

    try {
      const tournamentData = {
        tournamentName: formData.tournamentName,
        description: formData.description,
        startDate,
        endDate,
        registrationDeadline: formData.registrationDeadline || null,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        registrationFee: formData.registrationFee ? parseFloat(formData.registrationFee) : null,
        participantsType: formData.participantsType,
        playersPerTeam: formData.playersPerTeam ? parseInt(formData.playersPerTeam) : null,
        sportTypeId: formData.sportTypeId ? parseInt(formData.sportTypeId) : null,
        facilityId: formData.facilityId ? parseInt(formData.facilityId) : null,
        venueName: formData.venueName || null,
        venueAddress: formData.venueAddress || null,
        venuePlaceId: formData.venuePlaceId || null,
        venueLat: formData.venueLat || null,
        venueLng: formData.venueLng || null,
        city: formData.city || null,
        state: formData.state || null,
        country: formData.country || null,
        zipCode: formData.zipCode || null,
        streetNumber: formData.streetNumber || null,
        streetName: formData.streetName || null,
        landmark: formData.landmark || null,
        organizerUserId: user?.id || null,
        difficultyLevel: formData.difficultyLevel || null,
        prizeDetail: formData.prizeDetail || null,
        tournamentImage: formData.tournamentImage || null,
        matchConfiguration: formData.matchConfiguration || null,
        status: 'registration_open',
        playNow: formData.playNow || false,
        // Squad ownership — set when creating from a squad context
        ownerType: isSquadContext ? squadOwnerType : null,
        ownerId: isSquadContext ? squadOwnerId : null,
      };

      const result = await tournamentAPI.create(tournamentData);

      // Move temp image to canonical path now that we have the tournament ID
      if (pendingImageUrl && result?.id) {
        try {
          const { url: canonicalUrl } = await moveImage(pendingImageUrl, `tournament_assets/${result.id}/images/${crypto.randomUUID()}.jpeg`);
          await apiCall(`/tournaments/${result.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ tournamentImage: canonicalUrl }),
          });
        } catch (imgError) {
          console.error('[CreateTournamentPage] Image move failed after tournament creation:', imgError);
          // Non-fatal — tournament was created, image can be added later
        }
      }

      toast.success('Tournament created successfully!');
      if (squadOwnerType === 'academy') {
        navigate(`/academy/${squadOwnerId}?tab=tournaments`);
      } else if (isSquadContext) {
        navigate(`/squads/${squadOwnerId}/tournaments`, {
          state: { ownerName: squadOwnerName },
        });
      } else {
        navigate('/tournaments');
      }
    } catch (error) {
      console.error('Tournament creation failed:', error);
      toast.error('Failed to create tournament: ' + error.message);
    }
  };

  const handleBack = () => {
    if (squadOwnerType === 'academy') {
      navigate(`/academy/${squadOwnerId}?tab=tournaments`);
    } else if (isSquadContext) {
      navigate(`/squads/${squadOwnerId}/tournaments`, {
        state: { ownerName: squadOwnerName },
      });
    } else {
      navigate(-1);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-2xl mx-auto text-center">
          <Trophy className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
          <h1 className={`text-2xl font-bold ${theme.text} mb-4`}>Sign In Required</h1>
          <p className={`${theme.textSecondary} mb-6`}>You need to be signed in to create a tournament</p>
          <button
            onClick={() => navigate('/signin')}
            className={`${theme.primary} px-6 py-2 rounded-lg font-medium ${theme.primaryHover} transition-colors`}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={handleBack}
            className={`mr-4 p-2 ${theme.cardHover} rounded-lg transition-colors`}
          >
            <ArrowLeft className={`w-6 h-6 ${theme.textSecondary}`} />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${theme.text}`}>Create Tournament</h1>
            <p className={`${theme.textSecondary} mt-1`}>Set up your sports tournament or league</p>
          </div>
        </div>

        {/* Owner context banner (squad or academy) */}
        {isSquadContext && (
          <div className="mb-6 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg flex items-center gap-x-3">
            <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
            <p className="text-sm text-purple-700 dark:text-purple-300">
              This tournament will be created for{' '}
              <span className="font-semibold">
                {squadOwnerName || (squadOwnerType === 'academy' ? 'this academy' : 'your squad')}
              </span>.
            </p>
          </div>
        )}

        {/* Form */}
        <TournamentForm
          ref={tournamentFormRef}
          onSubmit={handleSubmit}
          onCancel={handleBack}
          submitLabel="Create Tournament"
          onImageSelected={handleImageSelected}
          initialData={squadSportTypeId ? { sportTypeId: squadSportTypeId } : {}}
        />

        {/* Image Crop Modal */}
        <ImageCropModal
          isOpen={cropModalOpen}
          onClose={() => {
            setCropModalOpen(false);
            setSelectedImageForCrop(null);
          }}
          imageSrc={selectedImageForCrop}
          fileType="tournament_assets"
          onCropComplete={handleCroppedImage}
          shape="rect"
          aspectRatio={16 / 9}
        />
      </div>
    </div>
  );
}
